import {toMoneyFromCents, type Money} from "../../../../../lib/Money.ts";
import type {DocumentSettlement} from "../../types/types.ts";
import type {PanelBooking} from "../types.ts";
import type {GroupingAxis} from "./groupingAxis.ts";
import type { CurrencyEnum } from '../../../../../generated/enums.ts';

export interface BookingGroup {
    key: string;
    label: string;
    settlement: DocumentSettlement | null;
    net: Money;
    bookings: PanelBooking[];
}

export interface AxisGroupIndex {
    keyByBooking: Map<string, string>;
    netByKey: Map<string, Money>;
    settlementByKey: Map<string, DocumentSettlement>;
    labelByKey: Map<string, string>;
}

function addNode(parents: Map<string, string>, node: string) {
    if (!parents.has(node)) parents.set(node, node);
}

function find(parents: Map<string, string>, node: string): string {
    let root = node;
    while (parents.get(root) !== root) {
        root = parents.get(root)!;
    }
    let cursor = node;
    while (cursor !== root) {
        const next = parents.get(cursor)!;
        parents.set(cursor, root);
        cursor = next;
    }
    return root;
}

function union(parents: Map<string, string>, left: string, right: string) {
    const leftRoot = find(parents, left);
    const rightRoot = find(parents, right);
    if (leftRoot !== rightRoot) parents.set(leftRoot, rightRoot);
}

function groupLabelOf(bookings: PanelBooking[]): string {
    const first = bookings[0];
    return first?.title || first?.subtitle || "Onbekende bron";
}

export function buildAxisGroupIndex(
    bookings: PanelBooking[],
    axis: GroupingAxis,
    settlements: DocumentSettlement[],
    currency: CurrencyEnum,
): AxisGroupIndex {
    const parents = new Map<string, string>();
    for (const booking of bookings) {
        const rowNode = "row:" + booking.key;
        addNode(parents, rowNode);
        for (const id of booking.axisIds[axis]) {
            const idNode = "id:" + id;
            addNode(parents, idNode);
            union(parents, rowNode, idNode);
        }
    }

    const membersByRoot = new Map<string, PanelBooking[]>();
    const idsByRoot = new Map<string, Set<string>>();
    for (const booking of bookings) {
        const root = find(parents, "row:" + booking.key);
        const members = membersByRoot.get(root);
        if (members) members.push(booking);
        else membersByRoot.set(root, [booking]);
        const ids = idsByRoot.get(root) ?? new Set<string>();
        for (const id of booking.axisIds[axis]) ids.add(id);
        idsByRoot.set(root, ids);
    }

    const settlementByBill = new Map<string, DocumentSettlement>();
    for (const settlement of settlements) {
        for (const billId of settlement.bill_ids) {
            settlementByBill.set(billId, settlement);
        }
    }

    const keyByBooking = new Map<string, string>();
    const netByKey = new Map<string, Money>();
    const settlementByKey = new Map<string, DocumentSettlement>();
    const labelByKey = new Map<string, string>();

    for (const [root, members] of membersByRoot) {
        const ids = [...(idsByRoot.get(root) ?? [])].sort();
        const key = ids.length > 0 ? `${axis}:${ids.join(",")}` : `row:${members[0].key}`;
        const cents = members.reduce(
            (sum, booking) => sum + booking.credit.signed_amount - booking.debit.signed_amount,
            0,
        );
        netByKey.set(key, toMoneyFromCents(cents, currency));
        labelByKey.set(key, groupLabelOf(members));
        const settlement = ids
            .map((id) => settlementByBill.get(id))
            .find((found): found is DocumentSettlement => found !== undefined);
        if (settlement) settlementByKey.set(key, settlement);
        for (const booking of members) {
            keyByBooking.set(booking.key, key);
        }
    }

    return {keyByBooking, netByKey, settlementByKey, labelByKey};
}

export function groupVisibleBookings(
    bookings: PanelBooking[],
    index: AxisGroupIndex,
    currency: CurrencyEnum,
): BookingGroup[] {
    const order: string[] = [];
    const membersByKey = new Map<string, PanelBooking[]>();
    for (const booking of bookings) {
        const key = index.keyByBooking.get(booking.key);
        if (key === undefined) continue;
        const existing = membersByKey.get(key);
        if (existing) {
            existing.push(booking);
        } else {
            membersByKey.set(key, [booking]);
            order.push(key);
        }
    }

    const groups = order.map((key) => ({
        key,
        label: index.labelByKey.get(key) ?? "Onbekende bron",
        settlement: index.settlementByKey.get(key) ?? null,
        net: index.netByKey.get(key) ?? toMoneyFromCents(0, currency),
        bookings: membersByKey.get(key)!,
    }));

    groups.sort((a, b) => Math.abs(b.net.signed_amount) - Math.abs(a.net.signed_amount));
    return groups;
}

export function openGroups(groups: BookingGroup[]): BookingGroup[] {
    return groups.filter((group) => group.net.signed_amount !== 0);
}
